import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ThemeProvider } from './contexts/ThemeContext';

// Layouts
import { DashboardLayout } from './features/dashboard/DashboardLayout';

// Features
import { AuthForm } from './features/auth/AuthForm';

// Pages
import { Home } from './pages/Home';
import { Dashboard } from './pages/Dashboard';
import LeaseAnalysis from './pages/LeaseAnalysis';
import Onboarding from './pages/Onboarding';
import LegalGate from './pages/legal/LegalGate';
import PaymentStatus from './pages/PaymentStatus';
import Terms from './pages/legal/Terms';
import Disclaimer from './pages/legal/Disclaimer';
import RefundPolicy from './pages/legal/RefundPolicy';
import TermsDeclined from './pages/legal/TermsDeclined';
import StyleGuide from './pages/StyleGuide';
import Welcome from './pages/lw/Welcome';
import LwStyleGuide from './pages/lw/StyleGuide';
import LocalAttorneys from './pages/lease/LocalAttorneys';
import Toolkit from './features/toolkit/Toolkit';

// Services
import { auth } from './services/auth';

// Redirect to /login when no JWT is present.
function RequireAuth({ children }) {
  if (!auth.isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      suspense: true,
    },
  },
});

export function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-200">
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<AuthForm />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/disclaimer" element={<Disclaimer />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/terms-declined" element={<TermsDeclined />} />
                <Route path="/style-guide" element={<StyleGuide />} />
                <Route path="/lw/welcome" element={<Welcome />} />
                <Route path="/lw/style-guide" element={<LwStyleGuide />} />
                <Route
                  path="/legal"
                  element={
                    <RequireAuth>
                      <LegalGate />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/payment-status"
                  element={
                    <RequireAuth>
                      <PaymentStatus />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/attorneys"
                  element={
                    <RequireAuth>
                      <LocalAttorneys />
                    </RequireAuth>
                  }
                />

                {/* Protected routes */}
                <Route
                  element={
                    <RequireAuth>
                      <DashboardLayout />
                    </RequireAuth>
                  }
                >
                  <Route
                    path="/dashboard"
                    element={
                      <ErrorBoundary>
                        <Dashboard />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="/lease-analysis"
                    element={
                      <ErrorBoundary>
                        <LeaseAnalysis />
                      </ErrorBoundary>
                    }
                  />
                  <Route
                    path="/toolkit"
                    element={
                      <ErrorBoundary>
                        <Toolkit />
                      </ErrorBoundary>
                    }
                  />
                </Route>

                {/* Fallback route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </BrowserRouter>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
